/**
 * Spec 027 — Team-flow API client.
 *
 * Wraps the new `PATCH /api/orgs/{slug}/runs/{run_id}/teams/{team_id}`
 * endpoint introduced by spec 027 (FR-009a + FR-009f).
 */
import apiClient from './client';
import type { TeamMember, TeamRenameRequest } from './types';

export interface TeamPatchResult {
  id: string;
  organization_id: string;
  run_id: string;
  name: string | null;
  color: string | null;
  member_count: number;
  share_code: string | null;
  /**
   * Spec 027 — members payload is returned only when the caller is allowed to
   * see the team share-code (creator+, coordinator+, or members of the team).
   * Other callers get `null` and must rely on `member_count` only.
   */
  members: TeamMember[] | null;
}

/**
 * Commit (or update) a team's display name.
 *
 * Errors raised by the underlying axios client (`ApiError`) carry the HTTP
 * status; callers map 403/409/422 to the inline-error variants per the
 * contract in `.specify/specs/027-team-flow-v2/contracts/http-patch-team.md`.
 */
export async function patchTeamName(args: {
  orgSlug: string;
  runId: string;
  teamId: string;
  name: string;
}): Promise<TeamPatchResult> {
  const body: TeamRenameRequest = { name: args.name };
  const { data } = await apiClient.patch<TeamPatchResult>(
    `/api/orgs/${args.orgSlug}/runs/${args.runId}/teams/${args.teamId}`,
    body,
  );
  return data;
}

/** GET /api/orgs/{slug}/runs/{run_id}/teams/{team_id} — used as polling fallback. */
export async function getTeam(args: {
  orgSlug: string;
  runId: string;
  teamId: string;
}): Promise<TeamPatchResult> {
  const { data } = await apiClient.get<TeamPatchResult>(
    `/api/orgs/${args.orgSlug}/runs/${args.runId}/teams/${args.teamId}`,
  );
  return data;
}

/**
 * Spec 027 — broadcast a creator-stage transition (Invite → PickName) so
 * already-joined teammates can advance their Waiting screen to Pending in
 * lockstep with the creator. Best-effort: callers should swallow non-200s,
 * the wait-screen does not actually block on this signal.
 */
export async function advanceTeamStage(args: {
  orgSlug: string;
  runId: string;
  teamId: string;
  stage: 'naming';
}): Promise<void> {
  await apiClient.post(
    `/api/orgs/${args.orgSlug}/runs/${args.runId}/teams/${args.teamId}/advance-stage`,
    { stage: args.stage },
  );
}
